import * as React from "react";
import { modelKeyToDealTextMap, a11yKeyToTextMap, buildDatesLabel } from "../checkPricesLocalization";
import { TrackedLink } from "src/components/utility/analytics/TrackedLink";
import { DealProps } from "../typings";
import { UitkCard, UitkCardContentSection, UitkCardActionArea, UitkCardContentSectionProps } from "uitk-react-cards";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkText } from "uitk-react-text";
//import { UitkLink } from "uitk-react-link";
import { viewContext } from "../CheckPrices";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { useLocalization } from "@shared-ui/localization-context";
import { HotelSearchLinkBuilder } from "components/utility/HotelSearchLinkBuilder/HotelSearchLinkBuilder";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { HotwireDealView } from "./views/HotwireDealView";

export const Deal: React.FC<DealProps> = ({ border, modelKey, model, context }) => {
  if (!modelKeyToDealTextMap[modelKey] || !model[modelKey]) {
    return null;
  }

  const { searchContext } = context;

  const content = model[modelKey];
  const l10n = useLocalization();
  const { formatText } = l10n;
  const { name, type } = searchContext?.location;
  const datesLabel = buildDatesLabel(l10n, content.isoStartDate, content.isoEndDate);
  const data = model.dataType === "dates" ? datesLabel : content.price || formatText("checkPrices.label.getPrice");

  const cardLink = getCardLink(modelKey, content.link, context);

  const props = {
    a11yText: a11yKeyToTextMap[modelKey]({ regionName: name, datesLabel, regionType: type, l10n }),
    cardLink,
    label: modelKeyToDealTextMap[modelKey](l10n),
    data,
    modelKey,
    border,
  };

  const view = React.useContext(viewContext);

  switch (view) {
    case "horizontal-data-display":
      return <RowViewDeal {...props} />;
    case "horizontal-card-carousel":
      return <CarouselViewDeal {...props} />;
    case "hotwire":
      return (
        <HotwireDealView
          label={model[modelKey].label}
          buttonLink={model[modelKey].link}
          modelKey={modelKey}
          context={context}
        />
      );
    default:
      return <DealWrapper {...props} />;
  }
};

interface DealViewProp {
  a11yText: string;
  cardLink: string;
  data: string;
  label: string;
  modelKey: string;
  border?: boolean;
}

interface DealWrapperProp {
  a11yText: string;
  cardLink: string;
  data: string;
  label: string;
  modelKey: string;
  border?: boolean;
}

const getContentProps = (border: boolean): UitkCardContentSectionProps => (border ? { border: "top" } : {});

const DealWrapper: React.FC<DealWrapperProp> = (props) => (
  <UitkSpacing key={props.modelKey}>
    <UitkCardContentSection {...getContentProps(!!props.border)}>
      <DefaultDeal {...props} />
    </UitkCardContentSection>
    <UitkCardActionArea>
      <TrackedLink className="uitk-card-link" moduleName="check-prices" rfrr={props.modelKey} href={props.cardLink}>
        {props.a11yText}
      </TrackedLink>
    </UitkCardActionArea>
  </UitkSpacing>
);

const DefaultDeal: React.FC<DealViewProp> = ({ a11yText, cardLink, data, label, modelKey }) => (
  <UitkLayoutGrid columns={2} space="four">
    <UitkLayoutGridItem>
      <UitkText theme="default" size={300}>
        <TrackedLink
          moduleName="check-prices"
          className="uitk-text-default-theme"
          rfrr={modelKey}
          href={cardLink}
          style={{ textDecoration: "none" }}
        >
          {label}
        </TrackedLink>
      </UitkText>
    </UitkLayoutGridItem>
    <UitkLayoutGridItem justifySelf="end" alignSelf="center">
      <div>
        <UitkText size={300}>
          <TrackedLink tabIndex="-1" className="uitk-link" moduleName="check-prices" rfrr={modelKey} href={cardLink}>
            {data}
          </TrackedLink>
        </UitkText>
        <TrackedLink moduleName="check-prices" rfrr={modelKey} href={cardLink} className={"is-visually-hidden"}>
          {a11yText}
        </TrackedLink>
      </div>
    </UitkLayoutGridItem>
  </UitkLayoutGrid>
);

const RowViewDeal: React.FC<DealViewProp> = ({ a11yText, cardLink, data, label, modelKey }) => (
  <UitkLayoutFlexItem grow={1}>
    <UitkLayoutGrid>
      <UitkText spacing="one" weight="bold" size={300}>
        {label}
      </UitkText>
      <span className="is-visually-hidden">{a11yText}</span>
      <UitkText size={300}>
        <TrackedLink moduleName="check-prices_row" rfrr={modelKey} href={cardLink}>
          {data}
        </TrackedLink>
      </UitkText>
    </UitkLayoutGrid>
  </UitkLayoutFlexItem>
);

const CarouselViewDeal: React.FC<DealViewProp> = ({ a11yText, cardLink, data, label, modelKey }) => (
  <UitkCardContentSection className="view-carousel-card">
    <UitkCard>
      <UitkCardContentSection>
        <div>
          <UitkText size={300} weight="bold" align="center" spacing="one">
            <TrackedLink moduleName="check-prices_carouse" rfrr={modelKey} href={cardLink}>
              {data}
            </TrackedLink>
          </UitkText>
          <UitkText size={300} align="center" spacing="one">
            {label}
          </UitkText>
          <span className="is-visually-hidden">{a11yText}</span>
        </div>
      </UitkCardContentSection>
    </UitkCard>
  </UitkCardContentSection>
);

/**
 * This function handles creating the card link to HSR.
 * It is necessary because in the case of Today and Tomorrow, the back end module won't be returning the date as part of the URL
 * @param modelKey: to determine what deal we're dealing with.
 * @param backEndSuppliedLink: the url string built by the back-end module
 * @param context:
 */
const getCardLink = (modelKey: string, backEndSuppliedLink: string, context: ExtendedContextStore) => {
  const { formatDateString } = useLocalization();
  let cardLink = new HotelSearchLinkBuilder(backEndSuppliedLink).build(context); // may not contain dates for today or tomorrow (timezone differences)

  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);

  if (modelKey === "today") {
    cardLink = new HotelSearchLinkBuilder(backEndSuppliedLink)
      .withStartDate(formatDateString(today, { raw: "yyyy-MM-dd" }))
      .withEndDate(formatDateString(tomorrow, { raw: "yyyy-MM-dd" }))
      .build(context);
  }
  if (modelKey === "tomorrow") {
    const afterTomorrow = new Date();
    afterTomorrow.setDate(tomorrow.getDate() + 1);
    cardLink = new HotelSearchLinkBuilder(backEndSuppliedLink)
      .withStartDate(formatDateString(tomorrow, { raw: "yyyy-MM-dd" }))
      .withEndDate(formatDateString(afterTomorrow, { raw: "yyyy-MM-dd" }))
      .build(context);
  }

  return cardLink;
};
